import { useEffect, useState } from 'react';
import axiosNest from '../../api/axiosNest';

/**
 * Słownik stanów zlecenia z API (`GET /user-offers/states`).
 *
 * ## Po co, skoro `stanPlanowania.ts` ma listę stanów
 *
 * Od PR #92 słownik statusów jest konfiguracją w bazie: klient może dodać
 * stan wierszem albo zmienić etykietę. Front ma kopię KODÓW (kolory chipów,
 * etykiety awaryjne, strażnik zgodności z enumem backendu), ale etykiety
 * i lista do filtra mają pochodzić stąd — inaczej stan dodany przez klienta
 * pokazywałby się jako „Stan nieznany", a filtr by go nie znał.
 *
 * ## Jedno pobranie na sesję
 *
 * Wzorzec z `shared/spolki.ts`: pamięć modułowa + jedna obietnica w locie,
 * bo chip stanu montuje się w każdym wierszu listy. Brak odpowiedzi nie
 * wywraca ekranu — komponenty wracają do kopii z kodu.
 */

export interface DictionaryStatusEntry {
  code: string;
  label: string;
  sortOrder: number;
  terminal: boolean;
  active: boolean;
  department: string | null;
}

export const STATES_URL = '/user-offers/states';

let cached: DictionaryStatusEntry[] | null = null;
let inFlight: Promise<DictionaryStatusEntry[]> | null = null;

function isValidStatus(x: unknown): x is DictionaryStatusEntry {
  if (!x || typeof x !== 'object') return false;
  const s = x as Record<string, unknown>;
  return typeof s.code === 'string' && s.code !== '' && typeof s.label === 'string';
}

async function fetchStatuses(): Promise<DictionaryStatusEntry[]> {
  if (cached) return cached;
  if (inFlight) return inFlight;
  inFlight = axiosNest
    .get<{ states?: unknown }>(STATES_URL)
    .then((r) => {
      const list = Array.isArray(r.data?.states) ? r.data.states : [];
      // Wpis bez kodu albo etykiety nie jest stanem — pomijamy go, zamiast
      // pokazać pusty chip; reszta listy zostaje użyteczna.
      cached = list.filter(isValidStatus);
      return cached;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/** Do testów i po zmianie konfiguracji — następne użycie pobierze na nowo. */
export function forgetStatuses(): void {
  cached = null;
  inFlight = null;
}

export function useStatuses(): DictionaryStatusEntry[] {
  const [states, setStates] = useState<DictionaryStatusEntry[]>(cached ?? []);

  useEffect(() => {
    let isMounted = true;
    void fetchStatuses()
      .then((s) => {
        if (isMounted) setStates(s);
      })
      .catch(() => {
        if (isMounted) setStates([]);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  return states;
}
